import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Chip,
  Paper,
  Slider,
  Checkbox,
  FormControlLabel,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  FormHelperText,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import GeneratedJdResponse from "./GenerateJDResponse";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@tanstack/react-query";
import { useFormikContext } from "formik";
import { IGenerateJdModel } from "../../components/Playground/JobDescription";
import ReactQuill from "react-quill";
import ReactDOMServer from "react-dom/server";
import "react-quill/dist/quill.snow.css";
import { getMockCandidates } from "../../api/agent";
import { usePlaygroundContext } from "../Playground";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import MessagePopup from "../../layout/messagePopup";

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#050d2e",
    "& fieldset": {
      borderColor: "#050d2e",
    },
    "&:hover fieldset": {
      borderColor: "#000",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#050d2e",
    },
  },
  "& input": {
    fontSize: "14px",
    color: "#fff",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8c99cb",
  },
};

const textfieldstyle = {
  height: "42px",
  minHeight: "0px",
  "& .MuiOutlinedInput-root": {
    height: "42px",
    padding: 0,
    "&:hover fieldset": {
      borderColor: "transparent",
    },
    "&.Mui-focused fieldset": {
      borderColor: "transparent",
    },
  },
  "& input": {
    fontSize: "14px",
    color: "#fff",
    lineHeight: "20px",
  },
};

interface CandidateSearchProps {
  handleClose: any;
}
const CandidateSearchForm: React.FC<CandidateSearchProps> = ({
  handleClose,
}) => {
  const playgroundContext = usePlaygroundContext();
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }
  const { playground, setPlayground } = playgroundContext;
  const [loadingPopupOpen, setLoadingPopupOpen] = useState(false);
  const citizen = ["Citizen"];
  const permanentResident = ["Permanent Resident"];
  const others = ["EAD", "H1B", "TN Visa", "Other"];
  const workAuthorization = playground.workAuthorization || {
    citizen: false,
    permanentResident: false,
    other: false,
  };
  const generatedResponse = playground.jobDescription;
  const responseTitle = playground.jobTitle ?? "";
  const { mutate: searchCandidates } = useMutation({
    mutationFn: getMockCandidates,
    onSuccess: (candidatesData: any) => {
      setPlayground((prev) => {
        return {
          ...prev,
          education: education,
          certifications: certifications,
          payrate: payrate,
          startdate: startDate,
          jobDescriptionExt: context.values.jobDescriptionExt,
        };
      });

      const formattedCandidates = candidatesData?.map((item: any) => {
        const generateJd = { ...generatedResponse.messageList[0] };
        if (city && state) {
          generateJd.location = `${city}, ${state}`;
        } else {
          generateJd.location = "";
        }
        generateJd.yearsOfExperience = searchData.experience;
        if (city && state) {
          generateJd.jobType = "Onsite";
        } else {
          generateJd.jobType = "Remote";
        }
        generateJd.payRate = Number(payrate);
        generateJd.job_shift_start = startDate ? startDate.format("YYYY-MM-DD")  : dayjs().add(15, 'day');
        const jobWorkAuthorization: string[] = [];
        if (workAuthorization.citizen) jobWorkAuthorization.push(...citizen);
        if (workAuthorization.permanentResident)
          jobWorkAuthorization.push(...permanentResident);
        if (workAuthorization.other) jobWorkAuthorization.push(...others);

        generateJd.jobWorkAuthorization = jobWorkAuthorization;

        return {
          eximiusId: item.search_results.eximiusId,
          firstName: item.search_results.firstName,
          lastName: item.search_results.lastName,
          yearsOfExperience: item.search_results.yearsOfExperience,
          location: item.search_results.location,
          difference_radius: Math.round(item.search_results.difference_radius),
          eximScore: item.eximScore,
          willingToRelocate: item.search_results.willingToRelocate,
          source: item.search_results.source,
          jobTitle: item.search_results?.jobTitle,
          scorecard: item.search_results.scorecard,
          JobDescription: generateJd.description,
          generateJd: generateJd,
          email: item.search_results.email,
          annualRate: item.search_results.annualRate,
          hotListed: item.search_results.hotListed,
          hourlyRate: item.search_results.hourlyRate,
          lastUpdated: item.search_results.lastUpdated,
          phone: item.search_results.phone,
          status: item.search_results.status,
        };
      });
      setPlayground((prev) => ({
        ...prev,
        candidates: formattedCandidates,
        page: 5,
      }));
      setCandidates(formattedCandidates);
      setShowCandidateMatch(true);
      setLoadingPopupOpen(false);
    },
    onError: (error: any) => {
      console.error("Error sending payload:", error);
      setLoadingPopupOpen(false);
    },
    onSettled: () => {
      setIsLoading(false);
      setLoadingPopupOpen(false);
    },
  });
  const [city, setCity] = useState(playground.city || "");
  const [state, setState] = useState(playground.state || "");
  const [payrate, setPayrate] = useState(playground.payrate || "");
  const [paytype, setPaytype] = useState(playground.paytype || "");
  const [education, setEducation] = useState<string[]>(
    playground.education || []
  );
  const [certifications, setCertifications] = useState<string[]>(
    playground.certifications || []
  );

  const [startDate, setStartDate] = useState<Dayjs | null>(
    playground.startDate ? dayjs(playground.startDate) : null
  );
  
  const handleStartDateChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isValid()) {
        setStartDate(newValue); // Set the state with the selected date
        setPlayground((prev) => ({
            ...prev,
            startDate: newValue.format("YYYY-MM-DD"), // Store in local format
        }));
    } else {
        setStartDate(null);
        setPlayground((prev) => ({
            ...prev,
            startDate: null,
        }));
    }
};
   

  const [inputCertification, setInputCertification] = useState("");
  const [inputEducation, setInputEducation] = useState("");
  const [payRateError, setPayRateError] = useState<string | null>(null);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [educationError, setEducationError] = useState<string | null>(null);
  const [experience, setExperience] = useState(0);
  const [showCandidateMatch, setShowCandidateMatch] = useState(false);
  const [focusedField, setFocusedField] = useState<
    | "City"
    | "state"
    | "Education"
    | "Certification"
    | "Payrate"
    | "Startdate"
    | "Paytype"
    | null
  >(null);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const context = useFormikContext<IGenerateJdModel>();

  const marks = [
    { value: 0, label: "0" },
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 15, label: "15" },
    { value: 20, label: "20" },
    { value: 25, label: "25" },
  ];

  const scrollToTop = () => {
    window.scroll(0, 0);
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (generatedResponse?.messageList[0]) {
      const jobDescription = generatedResponse.messageList[0];

      if (jobDescription.location && jobDescription.location !== "") {
        const locationParts = jobDescription.location.split(",");

        if (locationParts[0].trim().toLowerCase() !== "remote") {
          if (locationParts.length >= 1) setCity(locationParts[0].trim());
        }

        if (
          locationParts.length >= 2 &&
          locationParts[1].trim().toLowerCase() !== "remote"
        ) {
          setState(locationParts[1].trim());
        }
      }

      const educationList = Array.isArray(jobDescription?.education)
        ? jobDescription?.education?.map((edu: any) => edu?.degree) || [] // If it's an array, map over it
        : []; // If it's not an array, default to an empty array

      // Update the state
      setEducation((prevEducation) => [
        ...Array.from(new Set([...prevEducation, ...educationList])),
      ]);

      const certificationList =
        jobDescription?.certifications?.map((cert: any) => cert?.name) || [];
      setCertifications((prevCertifications) => [
        ...Array.from(new Set([...prevCertifications, ...certificationList])),
      ]);
    }
  }, [generatedResponse]);

  useEffect(() => {
    context.setFieldValue(
      "jobDescriptionExt",
      ReactDOMServer.renderToString(
        <GeneratedJdResponse
          data={generatedResponse?.messageList[0]}
        ></GeneratedJdResponse>
      )
    );
  }, [generatedResponse]);
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    setPlayground((prevState) => ({
      ...prevState,
      workAuthorization: {
        ...prevState.workAuthorization,
        [name]: checked,
      },
    }));
  };

  const handleAddCertification = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (
      ((event.key === "Enter" || event.key === ",") &&
        inputCertification.trim()) ||
      event.keyCode === 13
    ) {
      event.preventDefault();

      const certificationInput = inputCertification.trim();
      const newCertification = certificationInput
        .split(/[,;]+/)
        ?.map((cert) => cert.trim())
        .filter((cert) => cert.length > 0)[0];
      if (newCertification && !certifications.includes(newCertification)) {
        setCertifications((prevCerts) => [...prevCerts, newCertification]);
      }

      setInputCertification("");
    }
  };

  const handleAddCertificationOnblur = () => {
    if (inputCertification.trim()) {
      const certificationInput = inputCertification.trim();
      const newCertification = certificationInput
        .split(/[,;]+/)
        ?.map((cert) => cert.trim())
        .filter((cert) => cert.length > 0)[0];
      if (newCertification && !certifications.includes(newCertification)) {
        setCertifications((prevCerts) => [...prevCerts, newCertification]);
      }

      setInputCertification("");
    }
    setFocusedField(null);
  };

  const handleAddEducation = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      ((event.key === "Enter" || event.key === ",") && inputEducation.trim()) ||
      event.keyCode === 13
    ) {
      event.preventDefault();

      const educationInput = inputEducation.trim();
      const newEducation = educationInput
        .split(/[,;]+/)
        ?.map((edu) => edu.trim())
        .filter((edu) => edu.length > 0)[0];

      if (newEducation && !education.includes(newEducation)) {
        setEducation((prevEdu) => [...prevEdu, newEducation]);
      }

      setInputEducation("");
    }
  };


  const handleAddEducationOnBlur = () => {
    if (inputEducation.trim()) {
      const educationInput = inputEducation.trim();
      const newEducation = educationInput
        .split(/[,;]+/)
        ?.map((edu) => edu.trim())
        .filter((edu) => edu.length > 0)[0];

      if (newEducation && !education.includes(newEducation)) {
        setEducation((prevEdu) => [...prevEdu, newEducation]);
      }

      setInputEducation("");
    }
    setFocusedField(null);
  };

  const handleRemoveCertification = (certificationToRemove: string) => {
    setCertifications(
      certifications.filter((cert) => cert !== certificationToRemove)
    );
    setPlayground((prev) => {
      return {
        ...prev,
        certifications: certifications.filter(
          (cert) => cert !== certificationToRemove
        ),
      };
    });
  };
  const handleRemoveEducation = (educationToRemove: string) => {
    setEducation(education.filter((edu) => edu !== educationToRemove));
    setPlayground((prev) => {
      return {
        ...prev,
        education: education.filter((edu) => edu !== educationToRemove),
      };
    });
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    const newExperience = newValue as number;
    setExperience(newExperience);

    setPlayground((prevState) => ({
      ...prevState,
      experience: newExperience,
    }));
  };

  const searchData = {
    city,
    state,
    education,
    certifications,
    experience,
    workAuthorization,
    jobDescription: generatedResponse?.messageList[0],
    responseTitle,
  };
  const cleanString = (input: string) => {
    return input.trim().replace(/\s+/g, " ");
  };

  const remoteJob = city && state ? "n" : "y";
  const location = city && state ? `${city}, ${state}` : "";
  const search_radius = remoteJob === "n" ? 100 : null;
  const workAuth = [
    "Citizen",
    "Permanent Resident",
    "EAD",
    "H1B",
    "TN Visa",
    "Other",
  ];

  const handleSearchClick = async () => {
    const isValid = handleValidation();

    if (isValid) {
      setIsLoading(true);
      setLoadingPopupOpen(true);

      const cleanedJobTitle = cleanString(searchData.responseTitle);
      const finalStartDate = startDate ? startDate : dayjs().add(15, 'day');
      

      const jobWorkAuthorization: string[] = [];
      if (workAuthorization.citizen) jobWorkAuthorization.push(...citizen);
      if (workAuthorization.permanentResident)
        jobWorkAuthorization.push(...permanentResident);
      if (workAuthorization.other) jobWorkAuthorization.push(...others);

      // Check if jobWorkAuthorization is empty and assign WorkAuth if it is
      const finalJobWorkAuthorization =
        jobWorkAuthorization.length === 0 ? workAuth : jobWorkAuthorization;

      const matchpayload = {
        query_params: {
          jobExperience: searchData.experience,
          jobTitle: cleanedJobTitle,
          jobDescription: searchData.jobDescription?.description,
          education: searchData.education,
          skills: combinedSkills,
          certificationAndLicenses: searchData.certifications,
          location: location,
        },
        filter_params: {
          max_experience: null,
          jobWorkAuthorization: finalJobWorkAuthorization,
          max_package_value: Number(payrate),
          min_experience: searchData.experience || 0,
          remoteJob: remoteJob,
          search_radius: search_radius,
          n: 3,
          startDate: finalStartDate.format("YYYY-MM-DD"),
        },
      };

      searchCandidates(matchpayload);
    } else {
      console.log("Validation failed");
    }
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCC1M2XBigmfW-CFkAWCaYUSVmHl6a_-jc&libraries=places`;
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      const inputCity = document.getElementById(
        "city-input"
      ) as HTMLInputElement;
      const inputState = document.getElementById(
        "state-input"
      ) as HTMLInputElement;

      const autocompleteCity = new window.google.maps.places.Autocomplete(
        inputCity,
        {
          types: ["(cities)"],
          // componentRestrictions: { country: "us" },
        }
      );

      const autocompleteState = new window.google.maps.places.Autocomplete(
        inputState,
        {
          types: ["geocode"],
          // componentRestrictions: { country: "us" },
        }
      );

      const setCityStateFromPlace = (place: google.maps.places.PlaceResult) => {
        const addressComponents = place.address_components;

        addressComponents?.forEach((component) => {
          const types = component.types;
          if (types.includes("locality")) {
            const cityValue = component.long_name;
            setCity(cityValue || "Remote");
            setPlayground((prevState) => ({
              ...prevState,
              city: cityValue || "Remote",
            }));
          } else if (types.includes("administrative_area_level_1")) {
            const stateValue = component.short_name;
            setState(stateValue || "Remote");
            setPlayground((prevState) => ({
              ...prevState,
              state: stateValue || "Remote",
            }));
          }
        });
      };

      autocompleteCity.addListener("place_changed", () => {
        const place = autocompleteCity.getPlace();
        if (place.geometry) {
          setCityStateFromPlace(place);
        }
      });

      autocompleteState.addListener("place_changed", () => {
        const place = autocompleteState.getPlace();
        if (place.geometry) {
          setCityStateFromPlace(place);
        }
      });
    };
  }, []);

  const functionalSkills =
    generatedResponse?.messageList[0]?.functionalSkills?.map(
      (skill: { skill: any }) => skill.skill
    ) || [];
  const technicalSkills =
    generatedResponse?.messageList[0]?.technicalSkills?.map(
      (skill: { skill: any }) => skill.skill
    ) || [];

  const combinedSkills = [...functionalSkills, ...technicalSkills];

  const handleValidation = () => {
    let isValid = true;

    if (!payrate || isNaN(Number(payrate)) || Number(payrate) <= 0) {
      setPayRateError("Pay Rate is required and must be a positive number");
      isValid = false;
    } else {
      setPayRateError(null);
    }

    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/^\$/, "");
    const numericValue = value.replace(/\D/g, "");
    if (numericValue.length <= 4) {
      setPayrate(numericValue);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobilevalue = isMobile ? 2 : 8;
  const isMobilevalue1 = isMobile ? 2 : 4;
  const isMobilevalue2 = isMobile ? 4 : 9.8;
  const isMobilevalue3 = isMobile ? "150px" : "130px";
  const isMobilevalue4 = isMobile ? 6 : 12.6;
  const isMobilevalue5 = isMobile ? 0.8 : "12.6px";

  return (
    <Box p={isMobilevalue1} pt={0}>
      <Grid
        container
        spacing={2}
        sx={{
          py: {
            xs: 2, // For extra small screens
          },
          px: { xs: 2 },
        }}
      >
        <Grid item xs={12}>
          <Typography className="page-title">Search Candidates</Typography>
          <Typography fontSize={"16px"} sx={{ mb: 2 }}>
            Here's your Al-powered job description! Customize it to your
            needs-adding more details helps Eximius Al find the perfect
            candidates. Currently, our patent-pending intelligent matching
            algorithm searches from our ever-growing multi-million resume
            database, but once connected to your systems, it can also tap into
            your existing employee and candidate databases, plus applications
            from your job postings, whether on your career portal or external
            job boards. It's quick, easy, and tailored just for you!
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  mb: 2,
                  whiteSpace: "pre-line",
                }}
              >
                <ReactQuill
                  value={context.values.jobDescriptionExt}
                  onChange={(value) =>
                    context.setFieldValue("jobDescriptionExt", value)
                  }
                  theme="snow"
                  modules={{ toolbar: false }}
                  style={{
                    width: "100%",
                    height: "625px",
                    fontSize: "20px",
                    backgroundColor: "#050d2e",
                    color: "#fff",
                    scrollbarWidth: "thin",
                    scrollbarColor: "#263677 #0d1846",
                  }}
                />
                <style>
                  {`
      .ql-container::-webkit-scrollbar {
        width: 10px;
      }
      .ql-container::-webkit-scrollbar-thumb {
        background-color: #263677;
        border-radius: 10px; /* Ensuring rounded corners */
      }
      .ql-container::-webkit-scrollbar-track {
        background-color: #0d1846;
      }
      .ql-container::-webkit-scrollbar-button {
        display: none; /* Hide the up and down arrows */
      } 
    .ql-editor p strong {
      color: #538DFF  !important;
        margin-top: 10px; 
         display: inline-block;
     
    }
    `}
                </style>
              </Paper>
              <Typography sx={{ fontSize: "12px" }}>
                Edit the Al-generated job description to match your needs. More
                details help find better candidates.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid
                container
                spacing={1.5}
                sx={{ mb: 0, alignItems: "center", flexWrap: "nowrap" }}
              >
                <Grid item xs="auto">
                  <Typography
                    variant="subtitle1"
                    className="text-field-font"
                    sx={{ whiteSpace: "nowrap", ml: 1.9 }}
                  >
                    Job Location
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={6}
                  sx={{ flexGrow: 1, ml: isMobile ? 2.5 : 8.5, mb: 0 }}
                >
                  {/* Negative bottom margin to reduce gap */}
                  <TextField
                    className="field-textbox"
                    id="city-input"
                    fullWidth
                    value={city}
                    variant="outlined"
                    size="small"
                    placeholder="City"
                    sx={{
                      width: "100%", // Increased width
                      ...inputStyles,
                      ...textfieldstyle,
                      "& input::placeholder": {
                        color: "#b0b7c3 !important", // Change this to your desired color
                        opacity: 1, // Optional: To ensure full opacity
                      },
                    }}
                    onChange={(e) => setCity(e.target.value)}
                    onFocus={() => setFocusedField("City")}
                    onBlur={() => setFocusedField(null)}
                  />
                </Grid>

                <Grid item xs={6} sx={{ flexGrow: 1, mr: 5, mt: 0 }}>
                  {/* Negative top margin to reduce gap */}
                  <TextField
                    className="field-textbox"
                    id="state-input"
                    fullWidth
                    value={state}
                    variant="outlined"
                    size="small"
                    placeholder="State"
                    sx={{
                      width: "100%", // Increased width
                      ...inputStyles,
                      ...textfieldstyle,
                      "& input::placeholder": {
                        color: "#b0b7c3 !important",
                        opacity: 1,
                      },
                    }}
                    onChange={(e) => {
                      setState(e.target.value); 
                    }}
                    onFocus={() => setFocusedField("state")}
                    onBlur={() => setFocusedField(null)}
                  />
                </Grid>
              </Grid>

              {/* Education */}
              <Grid
                container
                spacing={2}
                alignItems="flex-start"
                sx={{ mt: 0 }}
              >
                <Grid item xs="auto">
                  <Typography
                    variant="subtitle1"
                    className="text-field-font"
                    sx={{ whiteSpace: "nowrap", ml: 2, mt: 1 ,pl:0}}
                  >
                    Education
                  </Typography>
                </Grid>

                <Grid item xs sx={{ ml: isMobilevalue2 }}>
                  <TextField
                    className="field-textbox"
                    fullWidth
                    value={inputEducation}
                    onChange={(e) => {
                      setInputEducation(e.target.value);
                      if (e.target.value.includes(",")) {
                        e.preventDefault();
                        handleAddEducationOnBlur();
                      }
                    }}
                    onKeyDown={handleAddEducation}
                    variant="outlined"
                    size="small"
                    sx={{
                      ...inputStyles,
                      ...textfieldstyle,
                      "& input::placeholder": {
                        color: "#b0b7c3 !important",
                        opacity: 1,
                      },
                    }}
                    onFocus={() => setFocusedField("Education")}
                    onBlur={() => handleAddEducationOnBlur()}
                    placeholder="Education"
                  />

                  {/* Chip List */}
                  <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                    {education
                      ?.filter((edu) => String(edu).trim() !== "")
                      .map((edu) => (
                        <Chip
                          key={edu}
                          label={
                            <Box
                              display="flex"
                              alignItems="center"
                              sx={{
                                whiteSpace: "normal", // Allow text to wrap
                                wordBreak: "break-word", // Ensure long words break properly
                                textAlign: "left", // Align text properly inside the Chip
                              }}
                            >
                              {edu}
                              <IconButton
                                size="small"
                                sx={{ ml: 0.5, p: 0 }}
                                onClick={() => handleRemoveEducation(edu)}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          }
                          sx={{
                            bgcolor: "#3b4c90",
                            color: "#fff",
                            fontSize: "12px",
                            height: "auto", // Allow Chip height to adjust based on text wrapping
                            padding: "6px", // Add some padding for better spacing
                            maxWidth: "100%", // Prevent overflow issues
                            display: "flex", // Ensure Chip content is aligned correctly
                            whiteSpace: "normal", // Allow text to wrap
                            wordBreak: "break-word", // Break long words properly
                          }}
                        />
                      ))}
                  </Box>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
                alignItems="flex-start"
                sx={{ mt: 2 }}
              >
                {/* Certification Label */}
                <Grid item xs="auto" sx={{mt:0}}>
                  <Typography
                    variant="subtitle1"
                    className="text-field-font"
                    sx={{ whiteSpace: "nowrap", ml: 2, mt: 1 }}
                  >
                    Certification
                  </Typography>
                </Grid>

                {/* Certification TextField + Chip List */}
                <Grid item xs sx={{ ml: isMobilevalue }}>
                  {/* TextField */}
                  <TextField
                    className="field-textbox"
                    fullWidth
                    value={inputCertification}
                    onChange={(e) => {
                      setInputCertification(e.target.value);
                      if (e.target.value.includes(",")) {
                        e.preventDefault();
                        handleAddCertificationOnblur();
                      }
                    }}
                    onKeyDown={handleAddCertification}
                    variant="outlined"
                    size="small"
                    sx={{
                      ...inputStyles,
                      ...textfieldstyle,
                      "& input::placeholder": {
                        color: "#b0b7c3 !important",
                        opacity: 1,
                      },
                      marginTop: 0,
                    }}
                    onFocus={() => setFocusedField("Certification")}
                    onBlur={() => handleAddCertificationOnblur()}
                    placeholder="Certification"
                  />

                  {/* Chip List */}
                  <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                    {certifications?.map((cert) => (
                      <Chip
                        key={cert}
                        label={
                          <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                              whiteSpace: "normal", // Allow text to wrap
                              wordBreak: "break-word", // Ensure long words break properly
                              textAlign: "left", // Align text properly inside the Chip
                            }}
                          >
                            {cert}
                            <IconButton
                              size="small"
                              sx={{ ml: 0.5, p: 0 }}
                              onClick={() => handleRemoveCertification(cert)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        }
                        sx={{
                          bgcolor: "#3b4c90",
                          color: "#fff",
                          fontSize: "12px",
                          height: "auto", // Allow Chip height to adjust based on text wrapping
                          padding: "6px", // Add some padding for better spacing
                          maxWidth: "100%", // Prevent overflow issues
                          display: "flex", // Ensure Chip content is aligned correctly
                          whiteSpace: "normal", // Allow text to wrap
                          wordBreak: "break-word", // Break long words properly
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
              <Box display="flex" alignItems="center" sx={{ mb: 0, px: 2 }}>
                {/* Minimum Experience Label */}
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1.2,
                    mt: 3,
                    whiteSpace: "nowrap",
                  }}
                  className="text-field-font"
                >
                  {/* Minimum Experience: {experience || playground.experience || 0}{" "}
                  yrs */}
                  Minimum Experience
                  <br />
                  <div style={{ minHeight: "24px", marginTop: 0, }}>
                    <span
                      style={{
                        color:
                          experience || playground.experience
                            ? "#538DFF"
                            : "transparent",
                      }}
                    >
                      {experience || playground.experience
                        ? `${experience || playground.experience} yrs`
                        : "0 yrs"}
                    </span>
                  </div>
                </Typography>

                {/* Slider */}
                <Slider
                  value={experience || playground.experience || 0}
                  onChange={handleSliderChange}
                  marks={marks}
                  min={0}
                  max={25}
                  valueLabelDisplay="auto"
                  sx={{
                    ml: 5,
                    flex: 1,
                    "& .MuiSlider-thumb": {
                      bgcolor: "white",
                    },
                    "& .MuiSlider-track": {
                      backgroundColor: "#192245",
                      border: "none",
                    },
                    "& .MuiSlider-rail": {
                      backgroundColor: "#192245",
                    },
                    "& .MuiSlider-markLabel": {
                      color: "#fff",
                    },
                  }}
                />
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} display="flex" alignItems="center">
                  <Typography
                    variant="subtitle1"
                    sx={{
                      whiteSpace: "nowrap",
                      mr: isMobile ? 6 : 2,
                      ml: 2,
                    }}
                    className="text-field-font"
                  >
                    Salary*
                  </Typography>

                  <TextField
                    className="field-textbox"
                    id="payrate-input"
                    value={payrate ? `$${payrate}` : ""} // Display the dollar sign with the value
                    variant="outlined"
                    size="small"
                    placeholder="Hourly Pay Rate"
                    sx={{
                      ...inputStyles,
                      ...textfieldstyle,
                      "& input::placeholder": {
                        color: "#b0b7c3 !important",
                        opacity: 1,
                      },
                      width: isMobilevalue3, // Adjust width as needed
                      ml: isMobilevalue4,
                    }}
                    onFocus={() => setFocusedField("Payrate")}
                    onBlur={() => setFocusedField(null)}
                    onChange={handleChange} // Use the handleChange function
                    inputProps={{ maxLength: 4 }}
                  />
                </Grid>

                {payRateError && (
                  <Grid item xs={12}>
                    <FormHelperText error>{payRateError}</FormHelperText>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2, // Space between label and DatePicker
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          whiteSpace: "nowrap",
                          ml: 1.9,
                          mr: 0.8,
                          minWidth: "100px", // Adjust width as needed
                        }}
                        className="text-field-font"
                      >
                        Tentative Start Date
                      </Typography>

                      <DemoContainer
                        components={["DatePicker"]}
                        sx={{
                          paddingTop: "0px",
                          backgroundColor: "#050d2e",
                          color: "#fff",
                          border: "1px solid rgba(66, 73, 101, 1)",
                          borderRadius: "4px",
                          height: "42px",
                          flexGrow: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginLeft: isMobilevalue5,
                        }}
                      >
                        <DatePicker
                          sx={{
                            width: "100%",
                            input: {
                              color: "#ffffff",
                              height: "42px",
                              padding: "0px 12px",
                              "&::placeholder": {
                                color: "#8c99cb", // Placeholder color
                              },
                            },
                            "& input::placeholder": {
                              color: "#b0b7c3 !important",
                              opacity: 1,
                            },
                            "& .MuiInputAdornment-root svg": {
                              color: "#ffffff",
                            },
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "transparent",
                              },
                              "&:hover fieldset": {
                                borderColor: "transparent",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "transparent",
                              },
                            },
                            "& .MuiOutlinedInput-root.Mui-focused input": {
                              color: "#ffffff",
                            },
                          }}
                          format="MM/DD/YY" // Date format
                          value={startDate} // Pass the state value here
                          onChange={handleStartDateChange} // Attach the change handler
                          slotProps={{
                            textField: {
                              placeholder: "MM/DD/YY", // Static placeholder
                            },
                          }}
                        />
                      </DemoContainer>
                    </Box>
                  </LocalizationProvider>
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row", // Keep items in a row
                  alignItems: "center", // Center items vertically
                  ml: 2.8,
                  flexWrap: isTablet ? "wrap" : "nowrap", // Allow wrapping on small screens
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: isTablet ? 0 : 7,
                    mt: 2,
                    ml: 0,
                    mr: 2,
                    whiteSpace: "nowrap",
                  }}
                  className="text-field-font"
                >
                  Work Authorization
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap", // Keep checkboxes in a single line
                    ml: 1, // Margin left for spacing
                  }}
                >
                  <FormControlLabel
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        marginLeft: "4px",
                        marginRight: "6px",
                      },
                    }}
                    control={
                      <Checkbox
                        style={{
                          borderColor: "#0066f4",
                          color: workAuthorization.citizen ? "#0066f4" : "grey",
                        }}
                        checked={workAuthorization.citizen}
                        onChange={handleCheckboxChange}
                        name="citizen"
                      />
                    }
                    label="Citizen"
                  />
                  <FormControlLabel
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        marginLeft: "4px",
                        marginRight: "6px",
                        display: "block",
                        whiteSpace: "nowrap", // Prevent default wrapping
                        "@media (max-width: 20px)": {
                          whiteSpace: "normal", // Allow wrapping for smaller screens
                          display: "inline", // Force the label to break into two lines
                        },
                      },
                    }}
                    control={
                      <Checkbox
                        style={{
                          color: workAuthorization.permanentResident
                            ? "#0066f4"
                            : "grey",
                        }}
                        checked={workAuthorization.permanentResident}
                        onChange={handleCheckboxChange}
                        name="permanentResident"
                      />
                    }
                    label="Permanent Resident"
                  />

                  <FormControlLabel
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        marginLeft: "2px",
                        marginRight: "6px",
                      },
                    }}
                    control={
                      <Checkbox
                        style={{
                          color: workAuthorization.other ? "#0066f4" : "grey",
                        }}
                        checked={workAuthorization.other}
                        onChange={handleCheckboxChange}
                        name="other"
                      />
                    }
                    label="Other"
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  ml: 3,
                  mt: 3,
                  flexWrap: "nowrap",
                  overflowX: "auto",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="outlined"
                  type="button"
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#7a7a7a",
                    borderColor: "rgb(203, 213, 225)",
                    color: "#fff",
                    height: "44px",
                    borderRadius: "45px",
                    padding: "12px 20px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={()=>{
                    setPlayground((playground) => ({
                      ...playground,
                      startDate: null,
                      payrate: "",
                    }))
                    setStartDate(null)
                    setPayrate(null)
                    handleClose()
                  }}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    height: "44px",
                    color: "#fff",
                    bgcolor: "#007BFF",
                    borderRadius: "45px",
                    padding: "12px 10px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    "&:hover": {
                      bgcolor: "#0052CC",
                    },
                    "&:disabled": {
                      bgcolor: "#0052CC",
                      color: "#fff",
                    },
                  }}
                  onClick={handleSearchClick}
                  disabled={isLoading}
                  endIcon={isLoading && <CircularProgress size={20} />}
                >
                  Search Candidates
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <MessagePopup
        open={loadingPopupOpen}
        message="Our AI Engine is now searching through our database of 3.5 million candidates to find you the best matches. Please wait..."
      />
    </Box>
  );
};

export default CandidateSearchForm;
